import { Component } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, Home } from "lucide-react";

/**
 * Catches any uncaught render/DOM error and shows a calm Weave-styled fallback
 * instead of CRA's red overlay. Lets the user reload or go home without losing
 * saved work (sessions are persisted in MongoDB + localStorage).
 */
export default class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error("ErrorBoundary caught:", error, info);
    }

    handleReload = () => {
        this.setState({ hasError: false, error: null });
        window.location.reload();
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <div
                className="min-h-screen bg-weave-bg flex items-center justify-center px-6 py-16"
                data-testid="error-boundary"
            >
                <div className="max-w-lg w-full">
                    <p className="editorial-label text-weave-red mb-4">
                        Something snagged
                    </p>
                    <h1 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink leading-tight">
                        Sorry — that didn't quite work.
                    </h1>
                    <p className="mt-4 text-weave-slate leading-relaxed">
                        Your answers are safe. We've logged the error. Click
                        reload to pick up where you left off, or head home.
                    </p>
                    <div className="mt-8 flex gap-4 flex-wrap">
                        <button
                            onClick={this.handleReload}
                            className="btn-primary"
                            data-testid="error-reload"
                        >
                            <RotateCcw className="w-4 h-4" /> Reload
                        </button>
                        <Link
                            to="/"
                            onClick={() =>
                                this.setState({ hasError: false, error: null })
                            }
                            className="btn-secondary"
                            data-testid="error-home"
                        >
                            <Home className="w-4 h-4" /> Home
                        </Link>
                    </div>
                </div>
            </div>
        );
    }
}
